import { ref, watch } from 'vue'
import { TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_SWAP_EMPTY } from '~/constants'
import { BasicContainer } from '~/models/containers'
import type { TerminalName } from '~/services/apiClient'
import { loadContainersBasic } from '~/services/apiHelpers'
import { usedBasicContainersStore } from '~/stores/basicContainers'
import { useUserStore } from '~/stores/user'

interface UseSwapEmptyValidationProps {
  terminal: Ref<TerminalName>
  form: {
    empty_container_number: string
    shipping_line?: string
    container_type?: string
    chassis_number: string | null
  }
  formRules: {
    chassis_number: Array<{
      required: boolean
      message: string
      trigger: string
    }>
  }
}

export function useSwapEmptyValidation(props: UseSwapEmptyValidationProps) {
  const basicContainersStore = usedBasicContainersStore()
  const userStore = useUserStore()

  const selectedContainer = ref<BasicContainer | null>(null)
  const loadingEmptyData = ref(false)
  const isOwnChassis = ref(shouldUseOwnChassisAsDefault())

  function shouldUseOwnChassisAsDefault(): boolean {
    if (
      !TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_SWAP_EMPTY.has(
        props.terminal.value
      )
    ) {
      return false // Not even an option, just return false
    } else {
      // Use whatever settings are. This has its own logic
      return userStore.useOwnChassis
    }
  }

  // Watch for chassis validation rule updates
  watch([props.terminal, isOwnChassis], () => {
    if (
      TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_SWAP_EMPTY.has(
        props.terminal.value
      ) &&
      isOwnChassis.value === true
    ) {
      props.formRules.chassis_number[0].required = true
    } else {
      props.formRules.chassis_number[0].required = false
    }
  })

  async function validateEmptyContainer() {
    let container = basicContainersStore.containersLookup.get(
      props.form.empty_container_number
    )
    if (!container) {
      loadingEmptyData.value = true
      try {
        const resp = await loadContainersBasic({
          containers: props.form.empty_container_number,
        })
        if (resp.data.length > 0) {
          container = new BasicContainer(resp.data[0], userStore.demo_mode)
        }
      } finally {
        loadingEmptyData.value = false
      }
    }
    if (container) {
      selectedContainer.value = container
      if (container.container_type) {
        props.form.container_type = container.container_type
      }
      if (container.shipping_line) {
        props.form.shipping_line = container.shipping_line
      }
    } else {
      selectedContainer.value = null
    }
  }

  return {
    // Refs
    selectedContainer,
    loadingEmptyData,
    isOwnChassis,

    // Methods
    validateEmptyContainer,
  }
}
